import { FiBook } from 'react-icons/fi';

export const basicAdjectivesData = {
  courseId: 'basic-adjectives',
  courseType: 'vocabulary',
  title: 'Basic Adjectives',
  icon: FiBook,
  overview: {
    impact: 'Describe people, places, and things with precision',
    focus: 'Common adjectives organized by category',
    uses: 'Speaking all parts, Writing descriptions',
    duration: '20 minutes'
  },
  sections: [
    {
      title: 'Describing People',
      content: `<p>Appearance and personality adjectives:</p>`,
      points: [
        'Appearance: tall, short, slim, well-built, elderly, young, attractive',
        'Personality: friendly, shy, outgoing, generous, patient, honest, reliable',
        'Feelings: happy, sad, excited, nervous, confident, frustrated, grateful'
      ]
    },
    {
      title: 'Describing Places & Things',
      content: `<p>Size, quality, and opinion adjectives:</p>`,
      points: [
        'Size: huge, tiny, enormous, narrow, spacious, compact',
        'Quality: excellent, terrible, brilliant, awful, outstanding, mediocre',
        'Opinion: beautiful, ugly, fascinating, boring, delightful, dreadful'
      ]
    },
    {
      title: 'Adjective Order',
      content: `<p>When using multiple adjectives, follow this order:</p>`,
      points: [
        'Opinion → Size → Age → Shape → Color → Origin → Material → Purpose',
        'Example: \'a beautiful large old rectangular brown French wooden writing desk\'',
        'In practice, rarely use more than 2-3 adjectives together'
      ]
    }
  ],
  exercises: [
    {
      title: 'Choose the Best Adjective',
      type: 'select',
      instructions: 'Select the most appropriate word.',
      questions: [
        {
          text: 'The city was absolutely ___. I loved it!',
          options: [
            'fascinating',
            'big',
            'old'
          ],
          answer: 'fascinating',
          explanation: '\'Absolutely\' pairs with extreme adjectives like \'fascinating\'.'
        },
        {
          text: 'She is very ___ — always helping others.',
          options: [
            'generous',
            'tall',
            'quiet'
          ],
          answer: 'generous',
          explanation: 'Helping others = generous personality.'
        },
        {
          text: 'The room is too ___ for five people.',
          options: [
            'small',
            'bad',
            'old'
          ],
          answer: 'small',
          explanation: 'Not enough space → \'small\'.'
        }
      ]
    },
    {
      title: 'Choose the Opposite Adjective',
      type: 'select',
      instructions: 'Select the adjective that means the opposite.',
      questions: [
        {
          text: 'What is the opposite of \'big\'?',
          options: ['small', 'tall', 'wide', 'heavy'],
          answer: 'small',
          explanation: 'Big ↔ small. These are basic antonyms describing size.'
        },
        {
          text: 'What is the opposite of \'hot\'?',
          options: ['cold', 'warm', 'cool', 'mild'],
          answer: 'cold',
          explanation: 'Hot ↔ cold. These are extreme antonyms for temperature.'
        },
        {
          text: 'What is the opposite of \'generous\'?',
          options: ['selfish', 'shy', 'lazy', 'rude'],
          answer: 'selfish',
          explanation: 'Generous (giving freely) ↔ selfish (thinking only of yourself).'
        },
        {
          text: 'What is the opposite of \'fascinating\'?',
          options: ['boring', 'ugly', 'terrible', 'quiet'],
          answer: 'boring',
          explanation: 'Fascinating (extremely interesting) ↔ boring (not interesting at all).'
        }
      ]
    },
    {
      title: 'Complete with Correct Adjective',
      type: 'fill-in',
      instructions: 'Type the most appropriate adjective to complete the sentence.',
      questions: [
        {
          text: 'The exam was extremely ___. I couldn\'t answer a single question.',
          answer: 'difficult',
          explanation: '\'Difficult\' means hard to do. With \'extremely\', it shows the exam was very challenging.'
        },
        {
          text: 'My grandmother is very ___. She always listens to everyone\'s problems.',
          answer: 'patient',
          explanation: '\'Patient\' means able to wait calmly or tolerate difficulties without complaining.'
        },
        {
          text: 'The new apartment is quite ___. There\'s room for all my furniture.',
          answer: 'spacious',
          explanation: '\'Spacious\' means having a lot of space — more specific than just saying \'big\'.'
        },
        {
          text: 'The food at that restaurant was absolutely ___. We\'ll definitely go back.',
          answer: 'delicious',
          explanation: '\'Delicious\' is a strong adjective meaning very tasty. It pairs naturally with \'absolutely\'.'
        }
      ]
    },
    {
      title: 'Describe a Picture',
      type: 'writing',
      instructions: 'Write 1-2 sentences describing the scenario using at least two adjectives.',
      questions: [
        {
          text: 'Describe a busy city street during rush hour.',
          answer: 'busy, crowded, noisy',
          explanation: 'Example: \'The street was incredibly crowded and noisy during rush hour, with frustrated commuters trying to get home.\' Use varied adjectives to paint a vivid picture.'
        },
        {
          text: 'Describe a peaceful beach at sunset.',
          answer: 'peaceful, beautiful, calm',
          explanation: 'Example: \'The beach was absolutely beautiful at sunset, with calm waves and a warm golden light.\' Combining opinion + sensory adjectives creates richer descriptions.'
        },
        {
          text: 'Describe a person you admire.',
          answer: 'kind, intelligent, reliable',
          explanation: 'Example: \'My teacher is extremely intelligent and incredibly patient — she always explains things clearly.\' For IELTS Speaking, use personality + behaviour adjectives together.'
        }
      ]
    }
  ],
  tips: [
    'Learn adjectives in pairs (antonyms): big/small, hot/cold',
    'Use \'extreme\' adjectives for emphasis: good→excellent, bad→terrible',
    'Don\'t overuse \'good\', \'bad\', \'nice\' — use more specific words',
    'Practice: describe a photo using 5+ different adjectives'
  ],
  dailyPractice: [
    'Practice basic adjectives concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Basic Adjectives: Common descriptive words for people, places, and things. Organized by category with adjective order rules.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...basicAdjectivesData,
      title: basicAdjectivesData.title + ' (Foundations)',
      overview: { ...basicAdjectivesData.overview, duration: '15-20 minutes' },
      sections: basicAdjectivesData.sections.slice(0, Math.max(2, Math.ceil(basicAdjectivesData.sections.length / 2))),
      exercises: basicAdjectivesData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...basicAdjectivesData,
      title: basicAdjectivesData.title + ' (Advanced)',
      overview: { ...basicAdjectivesData.overview, duration: '30-35 minutes' },
      sections: basicAdjectivesData.sections,
      exercises: basicAdjectivesData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(basicAdjectivesData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return basicAdjectivesData;
}
